import * as React from "react"
import {observer} from "mobx-react"
import type {BoardStyleStruct, BoardUID} from "@cling/lib.shared.model"
import {background_style} from "@cling/lib.web.layout"
import {CSSTransition} from "react-transition-group"
import {board_background_image_url} from "@cling/lib.web.resources"

interface Props {
    board_style: BoardStyleStruct
    board_uid: BoardUID
}

/**
 * Render the current and the previous board's background and cross-fade.
 */
export const BoardBackground = observer(({board_uid, board_style}: Props) => {
    const [preloaded_url, set_preloaded_url] = React.useState("")
    const [preload_timeout, set_preload_timeout] = React.useState<any>()
    const [prev_board_style, set_prev_board_style] = React.useState<BoardStyleStruct>()
    const [prev_board_uid, set_prev_board_uid] = React.useState<BoardUID>()
    const [prev_board_style2, set_prev_board_style2] = React.useState<BoardStyleStruct>()
    React.useEffect(() => {
        if (
            JSON.stringify(board_style) !== JSON.stringify(prev_board_style) ||
            board_uid !== prev_board_uid
        ) {
            set_prev_board_uid(board_uid)
            set_prev_board_style(board_style)
            set_prev_board_style2(prev_board_style)
            clearTimeout(preload_timeout)
            set_preload_timeout(undefined)
        }
    }, [board_style, board_uid, prev_board_uid, prev_board_style, preload_timeout])
    if (!prev_board_style) {
        return null
    }
    const background_image_url = board_background_image_url(prev_board_style)
    const background_ready = !background_image_url || preloaded_url === background_image_url
    if (!background_ready && !preload_timeout) {
        // Make sure the background image is loaded before cross-fading the background images,
        // but don't wait for too long.
        const img = new Image()
        set_preload_timeout(setTimeout(() => preload_finished(background_image_url), 2000))
        const preload_finished = (url: string) => {
            if (background_image_url === url) {
                set_preloaded_url(url)
            }
        }
        img.onload = () => {
            clearTimeout(preload_timeout)
            set_preload_timeout(undefined)
            preload_finished(background_image_url)
        }
        img.src = background_image_url
    }
    return (
        <>
            <CSSTransition
                key={board_uid}
                classNames={{
                    appearActive: "board-background--enter-active",
                    appearDone: "board-background--enter-active",
                    enterActive: "board-background--enter-active",
                    enterDone: "board-background--enter-done",
                }}
                in={background_ready}
                appear
                exit={false}
                timeout={10}
            >
                <div
                    className="board-background"
                    style={{
                        ...background_style(
                            background_image_url,
                            prev_board_style.background_color,
                        ),
                    }}
                />
            </CSSTransition>
            {prev_board_style2 && (
                <div
                    className="board-background board-background--prev"
                    style={background_style(
                        prev_board_style2.background_image_url,
                        prev_board_style2.background_color,
                    )}
                />
            )}
        </>
    )
})
